/**
 * Results Summary Component
 * Displays coverage statistics and warehouse list after optimization
 */
import { Users, TrendingUp, Clock, MapPin, Lock } from 'lucide-react';
import type { CoverageResult, WarehouseLocation } from '../types';

interface ResultsSummaryProps {
  coverage: CoverageResult;
  warehouses: WarehouseLocation[];
}

export default function ResultsSummary({ 
  coverage, 
  warehouses, 
}: ResultsSummaryProps) {
  const statCardStyle = {
    padding: '0.75rem',
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    flex: 1,
    minWidth: '140px',
  };

  const statLabelStyle = {
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    marginBottom: '0.15rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const statValueStyle = {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
  };

  const getCoverageColor = (pct: number) => {
    if (pct >= 85) return '#22c55e';
    if (pct >= 70) return '#84cc16';
    if (pct >= 50) return '#eab308';
    return '#ef4444';
  };

  return (
    <div>
      {/* Coverage Stats Grid */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        flexWrap: 'wrap',
        marginTop: '-0.5rem',
        marginBottom: '0.6rem' 
      }}>
        <div style={statCardStyle}>
          <div style={statLabelStyle}>
            <Users size={14} />
            Total Population
          </div>
          <div style={statValueStyle}>
            {coverage.totalPopulation.toLocaleString()}
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={statLabelStyle}>
            <TrendingUp size={14} />
            2-Day Coverage
          </div>
          <div style={{ ...statValueStyle, color: getCoverageColor(coverage.pct2DayCoverage) }}>
            {coverage.pct2DayCoverage}%
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.1rem' }}>
            {coverage.population2Day.toLocaleString()} people
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={statLabelStyle}>
            <TrendingUp size={14} />
            3-Day Coverage
          </div>
          <div style={{ ...statValueStyle, color: getCoverageColor(coverage.pct3DayCoverage) }}>
            {coverage.pct3DayCoverage}%
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.1rem' }}>
            {coverage.population3Day.toLocaleString()} people
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={statLabelStyle}>
            <Clock size={14} />
            Avg Delivery
          </div>
          <div style={statValueStyle}>
            {coverage.avgDeliveryDays} days
          </div>
        </div>
      </div>

      {/* Warehouse List */}
      <div>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '0.5rem' 
        }}>
          {warehouses.map((wh, idx) => (
            <div
              key={wh.zip3}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.35rem',
                padding: '0.6rem',
                backgroundColor: wh.isRequired ? 'rgba(37, 99, 235, 0.1)' : 'var(--bg-secondary)',
                borderRadius: '6px',
                border: wh.isRequired ? '1px solid rgba(37, 99, 235, 0.3)' : '1px solid var(--border-color)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: wh.isRequired ? 'var(--primary)' : 'var(--bg-primary)',
                  border: '2px solid var(--primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: wh.isRequired ? '#fff' : 'var(--primary)',
                  flexShrink: 0,
                }}>
                  {idx + 1}
                </div>
                <div style={{ 
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  fontSize: '0.9rem',
                }}>
                  {wh.zip3}
                </div>
                {wh.isRequired && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.2rem',
                    fontSize: '0.6rem',
                    padding: '0.1rem 0.3rem',
                    backgroundColor: 'var(--primary)',
                    color: '#fff',
                    borderRadius: '3px',
                    marginLeft: 'auto',
                  }}>
                    <Lock size={8} />
                    Req
                  </span>
                )}
              </div>

              <div style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
              }}>
                <MapPin size={10} />
                {wh.city && wh.state ? `${wh.city}, ${wh.state}` : 'Pending'}
              </div>

              {wh.populationCovered !== undefined && wh.populationCovered > 0 && (
                <div style={{ 
                  fontSize: '0.75rem',
                  color: '#22c55e',
                  fontWeight: 600,
                  marginTop: '-0.15rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}>
                  {wh.populationCovered.toLocaleString()} <Users size={11} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

